// components/cards/ProductCard.jsx
import React, { useState } from 'react';
import { 
  ShoppingCart, Upload, X, AlertCircle, CheckCircle, 
  FileText, Loader2, Package, Pill, AlertTriangle,
  Plus, Minus
} from 'lucide-react';

const ProductCard = ({ product, pharmacy, user, onOrderSuccess }) => {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  const handleOrderClick = () => {
    if (!user) {
      alert('Please login to place an order');
      window.location.href = '/auth/login';
      return;
    }

    if (product.stock === 0) {
      alert('This product is currently out of stock');
      return;
    }

    setShowOrderModal(true);
  };

  const handleOrderComplete = () => {
    setShowOrderModal(false);
    setOrderSuccess(true);
    setTimeout(() => {
      setOrderSuccess(false);
      if (onOrderSuccess) onOrderSuccess();
    }, 3000);
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
        {/* Product Image */}
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-40 object-cover rounded-lg mb-3"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="w-full h-40 bg-gray-100 rounded-lg mb-3 flex items-center justify-center"
          style={{ display: product.image ? 'none' : 'flex' }}
        >
          <Package className="w-16 h-16 text-gray-400" />
        </div>

        {/* Product Info */}
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-900 line-clamp-1">{product.name}</h3>
          
          {product.genericName && (
            <p className="text-sm text-gray-500 line-clamp-1">{product.genericName}</p>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{product.form || 'Tablet'}</span>
            {product.dosage && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-sm text-gray-600">{product.dosage}</span>
              </>
            )}
          </div>

          <p className="text-green-600 font-bold text-lg">KES {product.price.toLocaleString()}</p>

          {/* Stock Status */}
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${
              product.stock === 0 ? 'text-red-600' : 
              product.stock < 10 ? 'text-orange-600' : 
              'text-green-600'
            }`}>
              {product.stock === 0 ? 'Out of Stock' : 
               product.stock < 10 ? `Limited Stock (${product.stock})` : 
               'In Stock'}
            </span>
            
            {product.prescriptionRequired && (
              <span className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                <Pill className="w-3 h-3" />
                Rx Required
              </span>
            )}
          </div>

          {/* Order Button */}
          <button
            onClick={handleOrderClick}
            disabled={product.stock === 0}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            <ShoppingCart className="w-4 h-4" />
            <span>Order Now</span>
          </button>
        </div>
      </div>

      {/* Order Modal */}
      {showOrderModal && (
        <OrderModal
          product={product}
          pharmacy={pharmacy}
          user={user}
          onClose={() => setShowOrderModal(false)}
          onSuccess={handleOrderComplete}
        />
      )}

      {/* Success Toast */}
      {orderSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg flex items-center gap-3 z-50 animate-slide-up">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-sm font-medium text-green-900">Order placed successfully!</p>
            <p className="text-xs text-green-700">The pharmacy will process your order shortly</p>
          </div>
        </div>
      )}
    </>
  );
};

// Order Modal Component
const OrderModal = ({ product, pharmacy, user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [prescription, setPrescription] = useState(null);
  const [prescriptionPreview, setPrescriptionPreview] = useState(null);
  const [notes, setNotes] = useState('');

  const subtotal = product.price * quantity;
  const deliveryFee = deliveryMethod === 'delivery' ? (pharmacy.deliveryFee || 200) : 0;
  const total = subtotal + deliveryFee;

  const handlePrescriptionChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPEG, PNG, and PDF files are allowed');
        return;
      }

      setPrescription(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPrescriptionPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPrescriptionPreview(null);
      }
      
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (product.prescriptionRequired && !prescription) {
      setError('Prescription is required for this medicine');
      return;
    }

    if (deliveryMethod === 'delivery' && !deliveryAddress.trim()) {
      setError('Delivery address is required');
      return;
    }

    if (quantity > product.stock) {
      setError(`Only ${product.stock} units available`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      

      const formData = new FormData();
      
      
      formData.append('medicineId', product._id);
      formData.append('pharmacyId', pharmacy._id);
      formData.append('quantity', quantity);
      formData.append('deliveryMethod', deliveryMethod);
      
      
      if (deliveryMethod === 'delivery') {
        formData.append('deliveryAddress', deliveryAddress);
      }
      
      if (notes) {
        formData.append('notes', notes);
      }
      
      
      if (prescription) {
        formData.append('file', prescription);
      }

      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Authentication required. Please login again.');
        setTimeout(() => {
          window.location.href = '/auth/login';
        }, 2000);
        return;
      }

      

      const response = await fetch('https://medigo-api.onrender.com/api/orders/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          
        },
        body: formData
      });

    

      let data;
      try {
        const text = await response.text();
       
        
        if (text) {
          data = JSON.parse(text);
          
        } else {
          throw new Error('Empty response from server');
        }
      } catch (parseError) {
        console.error(' Failed to parse response:', parseError);
        throw new Error('Invalid server response. Please try again.');
      }

      if (response.ok && data.success) {
        
        onSuccess();
      } else {
        console.error(' Order failed:', data);
        setError(data.message || data.error || 'Failed to place order. Please try again.');
      }
    } catch (err) {
      console.error(' Order error:', err);
      
      // More specific error messages
      if (err.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message.includes('NetworkError')) {
        setError('Unable to reach the server. Please try again later.');
      } else {
        setError(err.message || 'Failed to place order. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-gray-900">Place Order</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Product Info */}
          <div className="bg-gray-50 rounded-lg p-4 flex gap-4">
            {product.image && (
              <img src={product.image} alt={product.name} className="w-20 h-20 object-cover rounded" />
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{product.name}</h3>
              {product.genericName && (
                <p className="text-sm text-gray-600">{product.genericName}</p>
              )}
              <p className="text-lg font-bold text-green-600 mt-1">
                KES {product.price.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Prescription Warning */}
          {product.prescriptionRequired && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-900">Prescription Required</p>
                <p className="text-xs text-orange-700 mt-1">
                  This medicine requires a valid prescription from a licensed doctor
                </p>
              </div>
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                className="w-20 text-center px-3 py-2 border border-gray-300 rounded-lg"
                min="1"
                max={product.stock}
              />
              <button
                type="button"
                onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                (Max: {product.stock} available)
              </span>
            </div>
          </div>

          {/* Delivery Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Method
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDeliveryMethod('delivery')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  deliveryMethod === 'delivery'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="font-medium">Home Delivery</p>
                <p className="text-sm text-gray-600">KES {pharmacy.deliveryFee || 200}</p>
              </button>
              <button
                type="button"
                onClick={() => setDeliveryMethod('pickup')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  deliveryMethod === 'pickup'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="font-medium">Pickup</p>
                <p className="text-sm text-gray-600">Free</p>
              </button>
            </div>
          </div>

          {/* Delivery Address */}
          {deliveryMethod === 'delivery' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Address *
              </label>
              <textarea
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter your complete delivery address..."
                required={deliveryMethod === 'delivery'}
              />
            </div>
          )}

          {/* Prescription Upload */}
          {product.prescriptionRequired && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Prescription *
              </label>
              
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {prescription ? prescription.name : 'Click to upload prescription'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    JPEG, PNG, or PDF (Max 5MB)
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handlePrescriptionChange}
                  required={product.prescriptionRequired}
                />
              </label>

              {prescriptionPreview && (
                <img
                  src={prescriptionPreview}
                  alt="Prescription"
                  className="mt-3 max-h-48 rounded-lg border border-gray-200"
                />
              )}

              {prescription && prescription.type === 'application/pdf' && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span>{prescription.name}</span>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Instructions (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Any special instructions..."
            />
          </div>

          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal ({quantity} × KES {product.price})</span>
              <span className="font-medium">KES {subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Delivery Fee</span>
              <span className="font-medium">KES {deliveryFee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
              <span>Total</span>
              <span className="text-green-600">KES {total.toLocaleString()}</span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing Order...</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-5 h-5" />
                <span>Place Order</span>
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center">
            By placing this order, you agree to our terms and conditions
          </p>
        </form>
      </div>
    </div>
  );
};

export default ProductCard;